import * as R from 'ramda'
import {
  DBFind,
  DBFindOne,
  DBRemoveMany,
  DBClear,
  ReduxInsert,
  ReduxRemove,
} from '@weblite/persist'
import { Contact } from '@weblite/ts-action/contact'
import { CacheWithCalculatingMode } from '$helper/functions/cache'
import { createKeyvalStorage } from '$logic/lib/keyval.storage'
import { maxOfArray } from '$func/utils'
import { createStorage } from '$logic/lib/storage'

const LOCAL_ID_PREFIX = 'local_'
const COLLECTION = 'contacts'

const [storage, contactsReducer] = createStorage<Contact>(COLLECTION)

// NOTE: This amount is approximate due to the expense of being exact and will update only once contacts are saved or edited.
const contactUpdateStorage = createKeyvalStorage<{
  latestUpdateTime: number
}>('contact-update')

function compareAndSeLastUpdateTime(contacts: Contact[]) {
  const currentValue = contactUpdateStorage.get()?.latestUpdateTime || 0
  const maxUpdated =
    R.prop('updatedAt', maxOfArray(R.prop('updatedAt'), contacts)) || 0

  if (maxUpdated > currentValue) {
    contactUpdateStorage.set({ latestUpdateTime: maxUpdated })
    return maxUpdated
  }

  return currentValue
}

const nullValues = new Set()
const cachedNonSignedUp = new CacheWithCalculatingMode<Contact[]>()
let signedUpContactsLoaded = false

const contactsStorage = {
  getAll: storage.getAll,
  async get(id: string) {
    if (nullValues.has(id)) return null

    const res = await storage.get(id)
    if (!res) nullValues.add(id)
    return res
  },

  add(data: Parameters<typeof storage.add>[0]) {
    if (Array.isArray(data)) {
      data.forEach(({ id }) => nullValues.delete(id))
      compareAndSeLastUpdateTime(data)
    } else {
      nullValues.delete(data.id)
      compareAndSeLastUpdateTime([data])
    }

    return storage.add(data)
  },

  deleteByPhones(phones: string[]) {
    cachedNonSignedUp.reset()
    ReduxRemove(COLLECTION, { phone: { $in: phones } })
    return DBRemoveMany(COLLECTION, phones, 'phone')
  },

  deleteAll() {
    cachedNonSignedUp.reset()
    ReduxRemove(COLLECTION, {})
    return DBClear(COLLECTION)
  },

  findByPhone(phone: string) {
    return DBFindOne(COLLECTION, phone, 'phone') as Promise<Contact>
  },

  async getNonSignedUp() {
    const res = await cachedNonSignedUp.get()
    if (res) return res

    // NOTE: 'k' is id bigger than 'local_*' and never used in uuid.
    const keyRange = IDBKeyRange.lowerBound('k')
    const contacts = (await DBFind(COLLECTION, keyRange)) as Contact[]
    cachedNonSignedUp.set(contacts)
    return contacts
  },

  async loadSignedUpContacts() {
    if (signedUpContactsLoaded) return

    const keyRange = IDBKeyRange.upperBound('k')
    const contacts = (await DBFind(COLLECTION, keyRange)) as Contact[]
    ReduxInsert(COLLECTION, contacts)
    signedUpContactsLoaded = true
  },

  set(id: string, data: Parameters<typeof storage.set>[1]) {
    if (id.startsWith(LOCAL_ID_PREFIX)) cachedNonSignedUp.reset()
    compareAndSeLastUpdateTime([data as Contact])
    return storage.set(id, data)
  },

  del(id: Parameters<typeof storage.del>[0]) {
    cachedNonSignedUp.reset()
    return storage.del(id)
  },

  getLastUpdateTime() {
    const storedResult = contactUpdateStorage.get()?.latestUpdateTime
    if (Number.isInteger(storedResult)) return storedResult

    return compareAndSeLastUpdateTime(storage.getAll())
  },
}

export { contactsReducer, contactsStorage }
